/**
 * Desktop notifications via the Notification Web API.
 * Works on https:// origins and on http://localhost (browsers special-case localhost).
 */

const supported = typeof window !== 'undefined' && 'Notification' in window;

export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (!supported) return 'denied';
  if (Notification.permission === 'default') {
    try {
      return await Notification.requestPermission();
    } catch {
      return Notification.permission;
    }
  }
  return Notification.permission;
}

export interface ShowOpts {
  ticketId: number;
  title: string;
  body: string;
  onClickPath?: string;
}

/**
 * Show a notification. Returns silently if:
 *   - the Notification API isn't supported,
 *   - permission isn't granted, or
 *   - the tab is currently focused/visible (no point distracting the user with what they're already looking at).
 *
 * `tag` deduplicates: rapid updates for the same ticket replace the previous bubble rather than stacking.
 */
export function showTicketNotification(opts: ShowOpts): void {
  if (!supported) return;
  if (Notification.permission !== 'granted') return;
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;

  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      tag: `ticket-${opts.ticketId}`,
      // icon: '/favicon.ico',
    });
    n.onclick = () => {
      window.focus();
      if (opts.onClickPath) window.location.href = opts.onClickPath;
      n.close();
    };
  } catch {
    /* Notification constructor can throw in some browsers — swallow */
  }
}
